import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, Home, ExternalLink, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import TriangleScorecard from '@/components/TriangleScorecard';
import InlineStockModal from '@/components/InlineStockModal';
import AbbrTip from '@/components/AbbrTip';
import TriangleShareModal from '@/components/TriangleShareModal';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import SectionActions from '@/components/SectionActions';
import SiteDisclaimer from '@/components/SiteDisclaimer';

// Standalone shareable Triangle Scorecard page at /triangle/:ticker.
//
// Smart back/home button mirrors the CommunityTerms pattern:
//   - if the user came from within DiviDrip → "Back" (navigate(-1))
//   - if they landed cold (deep link / shared URL) → "Home" (navigate('/'))
//
// "Open full stock modal" CTA opens the in-place InlineStockModal on top
// of this same page — no navigation, so the URL stays shareable.
export default function TriangleStandalone() {
  const { ticker } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const tk = (ticker || '').toUpperCase();

  // Smart back/home detection. `document.referrer` is unreliable because
  // React Router does client-side navigation (no full page load → referrer
  // never updates), so a user clicking a Triangle chip on /portfolio would
  // still see "Home". React Router's `location.key` is the right signal:
  // it's literally the string 'default' only for the very first entry of a
  // browser session, and a unique opaque id for every subsequent in-app
  // navigation. That makes it the canonical "came from inside the app"
  // marker — independent of how the navigation happened (Link, navigate(),
  // chip click, modal CTA, etc).
  const [mode] = useState(() => (location.key && location.key !== 'default') ? 'back' : 'home');
  const [isDark, setIsDark] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  // When opened from the REIT ribbon's "Open Cash Lens" CTA we want
  // the modal to land directly on Stock Metrics (where the Cash Lens
  // panel lives) instead of the default Dividend Info tab. Tracked
  // separately so explicit "Open full stock view" still respects the
  // default landing tab.
  const [modalInitialTab, setModalInitialTab] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [triangleData, setTriangleData] = useState(null);
  // Round 95ac (Jun 2026) — wraps the Triangle + Buy Checklist +
  // Opinion blocks so the Copy / Print / PDF buttons (modelled on
  // the Insider & Institutional tab pattern) can capture exactly
  // those sections cleanly without bleeding the page chrome.
  const exportRef = useRef(null);

  useEffect(() => {
    const compute = () => setIsDark(
      typeof document !== 'undefined'
      && document.documentElement.classList.contains('dark')
    );
    compute();
    const obs = new MutationObserver(compute);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  // Round 79 — Esc keyboard shortcut now lives on the shared
  // BackOrHomeButton component (it owns both the back/home mode
  // detection AND the StockModal-restore on modalOrigin). The
  // inline binding here is kept only when share/modal dialogs are
  // open (their own Esc handlers steal focus), to defer cleanly.
  // The shared button itself sees `escDisabled={shareOpen || modalOpen}`
  // below and stays out of the way in that case.

  return (
    <div className="min-h-screen min-h-[100svh] bg-background">
      <div className="mx-auto max-w-3xl px-4 pt-6 sm:pt-8 pb-0" data-testid="triangle-standalone-page">
        <div className="flex items-center justify-between mb-4">
          <BackOrHomeButton
            testid="triangle-back-btn"
            escDisabled={shareOpen || modalOpen}
          />
          <div className="flex items-center gap-3">
            <Link
              to="/glossary#entry-dividend-triangle"
              target="_blank"
              rel="noopener"
              className="text-xs text-muted-foreground hover:text-foreground"
              data-testid="triangle-glossary-link"
              title="Open the Dividend Triangle definition in the Glossary"
            >
              What is the Triangle?
            </Link>
          </div>
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-1" data-testid="triangle-page-title">
          {tk} · Dividend Triangle
        </h1>
        <p className="text-sm text-muted-foreground mb-3">
          5-year compound growth across Revenue, <AbbrTip term="EPS">EPS</AbbrTip>, and Dividend — the three
          legs that decide whether a payout is sustainable.
        </p>

        {/* Round 95ac (Jun 2026) — Copy / Print / PDF action cluster
            mirroring the Insider & Institutional tab pattern. Captures
            the full Triangle Scorecard (Why-This-Score · Trap Flag ·
            Buy Checklist · Opinion · Radar) so users can hand a
            single-page brief to a friend, advisor, or their notes app. */}
        <div className="mb-4 flex items-center justify-end gap-3 flex-wrap">
          <SectionActions
            targetRef={exportRef}
            elementId={`triangle-scorecard-${tk}`}
            title={`${tk} · DiviDrip Dividend Triangle Scorecard`}
            filename={`${tk}-dividend-triangle-scorecard`}
            testid="triangle-standalone-actions"
          />
        </div>

        <div ref={exportRef} id={`triangle-scorecard-${tk}`} data-pdf-root>
          {/* Round 95af (Jun 2026) — PDF / Print header. Lives INSIDE
              the export wrapper so the captured document is self-
              identifying: ticker name, current price (from the
              just-loaded triangle payload), and the snapshot date.
              Without this, a user with several Triangle PDFs side-by-
              side has no way to tell which is which. The header is
              styled in muted "print chrome" colors so it doesn't
              shout against the dark cards below, but stays legible
              when html2canvas paints it white-background for the PDF. */}
          <div
            className="mb-3 pb-2 border-b border-border/60"
            data-testid="triangle-export-header"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div className="text-base sm:text-lg font-bold tracking-tight">
                {tk} · DiviDrip Dividend Triangle Scorecard
              </div>
              <div className="text-[11px] font-mono text-muted-foreground">
                Snapshot date: {new Date().toLocaleDateString(undefined, {
                  month: 'short', day: 'numeric', year: 'numeric',
                })}
              </div>
            </div>
            {triangleData && (triangleData.price != null || triangleData.name) && (
              <div className="mt-0.5 text-[11px] text-muted-foreground">
                {triangleData.name ? `${triangleData.name}` : ''}
                {triangleData.name && triangleData.price != null ? ' · ' : ''}
                {triangleData.price != null ? `$${Number(triangleData.price).toFixed(2)}` : ''}
              </div>
            )}
          </div>

          <TriangleScorecard
            ticker={tk}
            isDark={isDark}
            onDataLoaded={setTriangleData}
            onOpenStockModal={() => {
              setModalInitialTab('stock-metrics');
              setModalOpen(true);
            }}
          />
        </div>

        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={() => setShareOpen(true)}
            disabled={!triangleData}
            data-testid="triangle-share-card-btn"
          >
            <ImageIcon className="w-4 h-4 mr-1.5" /> Share card
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setModalOpen(true)}
            data-testid="triangle-open-modal-btn"
          >
            <ExternalLink className="w-4 h-4 mr-1.5" /> Open full stock view
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/triangle/compare?tickers=${tk}`)}
            data-testid="triangle-go-compare-btn"
          >
            Compare with another ticker
          </Button>
        </div>

        <div
          className="mt-8 max-w-prose mx-auto rounded-lg border-2 border-red-400 dark:border-red-700 px-5 py-4"
          data-testid="triangle-page-disclaimer"
        >
          <p className="text-xs font-medium leading-relaxed text-amber-900 dark:text-yellow-400 text-left">
            The Dividend Triangle is an educational visualization. It is not
            investment advice and does not replace your own research or a
            conversation with a licensed financial advisor.
          </p>
        </div>
      </div>
      <SiteDisclaimer />

      {/* In-place modal stays on the /triangle/:ticker URL — closing it
          returns the user to the standalone view without a navigation.
          `modalInitialTab` lets the REIT ribbon land users directly on
          the Stock Metrics tab where the Cash Lens panel lives. */}
      <InlineStockModal
        ticker={modalOpen ? tk : null}
        onClose={() => { setModalOpen(false); setModalInitialTab(null); }}
        initialTab={modalInitialTab || 'dividend-info'}
      />

      <TriangleShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        results={triangleData ? [triangleData] : []}
        mode="solo"
      />
    </div>
  );
}
